/**
 * Production-safe logger utility with PII sanitization
 * Only logs to console in development mode
 * In production, logs are sent to Sentry error tracking service
 * Automatically sanitizes sensitive data (PII, PHI, tokens, etc.)
 */

import * as Sentry from '@sentry/react';

const isDevelopment = import.meta.env.MODE === 'development';
const isProduction = !isDevelopment;

const AUTH_SENSITIVE_KEYS = [
    'password',
    'token',
    'authorization',
    'apiKey',
    'api_key',
    'accessToken',
    'refreshToken',
    'secret',
    'credentials',
    'cookie',
    'session',
] as const;

const PII_SENSITIVE_KEYS = [
    'patientId',
    'personId',
    'patientName',
    'personName',
    'firstName',
    'lastName',
    'email',
    'phone',
    'address',
    'ssn',
    'socialSecurityNumber',
    'mrn',
    'medicalRecordNumber',
    'dateOfBirth',
    'dob',
    'birthDate',
    'noteContent',
    'documentContent',
] as const;

/**
 * Sanitize error objects to remove sensitive information
 * Preserves useful fields for standard Error objects
 */
export function sanitizeError(error: unknown): unknown {
    if (!error || typeof error !== 'object') {
        return error;
    }

    // Preserve useful fields for standard Error objects.
    // Without this, Error instances often log as `{}` because their properties are non-enumerable.
    if (error instanceof Error) {
        return {
            name: error.name,
            message: error.message,
            stack: error.stack,
        };
    }

    const sensitiveKeys = [...AUTH_SENSITIVE_KEYS];

    const sanitize = (obj: Record<string, unknown>): Record<string, unknown> => {
        const sanitized: Record<string, unknown> = {};

        for (const [key, value] of Object.entries(obj)) {
            const lowerKey = key.toLowerCase();
            const isSensitive = sensitiveKeys.some(sensitiveKey => lowerKey.includes(sensitiveKey));

            if (isSensitive) {
                sanitized[key] = '[REDACTED]';
            } else if (value && typeof value === 'object' && !Array.isArray(value)) {
                sanitized[key] = sanitize(value as Record<string, unknown>);
            } else {
                sanitized[key] = value;
            }
        }

        return sanitized;
    };

    try {
        return sanitize(error as Record<string, unknown>);
    } catch {
        return error;
    }
}

/**
 * Sanitize data to remove PII/PHI and sensitive information
 */
function sanitizeData(data: unknown): unknown {
    if (!data || typeof data !== 'object') {
        return data;
    }

    if (data instanceof Error) {
        return sanitizeError(data);
    }

    const sensitiveKeys = [...AUTH_SENSITIVE_KEYS, ...PII_SENSITIVE_KEYS];

    const sanitize = (obj: Record<string, unknown>, depth = 0): Record<string, unknown> => {
        // Prevent infinite recursion
        if (depth > 10) {
            return { '[MAX_DEPTH_REACHED]': true };
        }

        const sanitized: Record<string, unknown> = {};

        for (const [key, value] of Object.entries(obj)) {
            const lowerKey = key.toLowerCase();
            const isSensitive = sensitiveKeys.some(sensitiveKey => lowerKey.includes(sensitiveKey.toLowerCase()));

            if (isSensitive) {
                sanitized[key] = '[REDACTED]';
            } else if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
                sanitized[key] = sanitize(value as Record<string, unknown>, depth + 1);
            } else if (Array.isArray(value)) {
                sanitized[key] = value.slice(0, 10).map(item => {
                    if (item && typeof item === 'object' && !Array.isArray(item) && !(item instanceof Date)) {
                        return sanitize(item as Record<string, unknown>, depth + 1);
                    }
                    return item;
                });
            } else {
                sanitized[key] = value;
            }
        }

        return sanitized;
    };

    try {
        return sanitize(data as Record<string, unknown>);
    } catch {
        return { '[SANITIZATION_ERROR]': true };
    }
}

function formatMessage(message: string, data?: unknown): string {
    if (data) {
        const sanitized = sanitizeData(data);
        return `${message} ${JSON.stringify(sanitized, null, isDevelopment ? 2 : 0)}`;
    }
    return message;
}

export const logger = {
    debug: (message: string, data?: unknown): void => {
        if (isDevelopment) {
            const formatted = formatMessage(message, data);
            // eslint-disable-next-line no-console
            console.debug(formatted);
        }
    },

    info: (message: string, data?: unknown): void => {
        if (isDevelopment) {
            const formatted = formatMessage(message, data);
            // eslint-disable-next-line no-console
            console.info(formatted);
        }
    },

    warn: (message: string, data?: unknown): void => {
        if (isDevelopment) {
            const formatted = formatMessage(message, data);
            console.warn(formatted);
        }
        if (isProduction) {
            if (data) {
                const sanitized = sanitizeData(data);
                Sentry.captureMessage(message, {
                    level: 'warning',
                    extra: sanitized as Record<string, unknown>,
                });
            } else {
                Sentry.captureMessage(message, {
                    level: 'warning',
                });
            }
        }
    },

    error: (message: string, error?: unknown): void => {
        if (isDevelopment) {
            const formatted = formatMessage(message, error);
            console.error(formatted);
        } else {
            if (error instanceof Error) {
                const sanitized = sanitizeError(error);
                Sentry.captureException(error, {
                    level: 'error',
                    tags: { customMessage: message },
                    extra: sanitized ? (sanitized as Record<string, unknown>) : undefined,
                });
            } else if (error) {
                const sanitized = sanitizeData(error);
                Sentry.captureMessage(message, {
                    level: 'error',
                    extra: sanitized as Record<string, unknown>,
                });
            } else {
                Sentry.captureMessage(message, {
                    level: 'error',
                });
            }
        }
    },

    table: (data: unknown): void => {
        if (isDevelopment) {
            const sanitized = sanitizeData(data);
            // eslint-disable-next-line no-console
            console.table(sanitized);
        }
    },
};

export default logger;
